<template>
  <div class="gps-reports-container">
    <spinner-loader :loadingStuff="loadingStuff" />

    <!-- Header -->
    <div class="header">
      <h3>GPS Reports Dashboard</h3>
      <p class="subtitle">Analyze team performance and GPS metrics</p>
    </div>

    <!-- Error Display -->
    <div v-if="error" class="alert alert-danger" role="alert">
      <strong>Error:</strong> {{ error }}
      <button @click="error = null" class="close-btn">&times;</button>
    </div>

    <!-- Team Selection -->
    <div class="selection-group">
      <div class="select-wrapper">
        <label for="team-select">Select Team:</label>
        <select id="team-select" v-model="selectedTeamId" @change="onTeamChange"
          :disabled="loadingStuff || teams.length === 0">
          <option :value="null">-- Select a team --</option>
          <option v-for="team in teams" :key="team.id" :value="team.id">
            {{ team.name }}
          </option>
        </select>
      </div>

      <!-- Session Selection -->
      <div v-if="selectedTeamId" class="select-wrapper">
        <label for="session-select">Select Session:</label>
        <select id="session-select" v-model="selectedSessionId" @change="onSessionChange"
          :disabled="loadingStuff || sessions.length === 0">
          <option :value="null">-- Select a session --</option>
          <option v-for="session in sessions" :key="session.id" :value="session.id">
            {{ formatSessionOption(session) }}
          </option>
        </select>
      </div>
    </div>

    <!-- Session Details -->
    <div v-if="selectedSession && athleteData.length > 0" class="session-details">
      <!-- Session Metadata -->
      <div class="session-header">
        <div class="session-info">
          <h4>
            <span class="session-type-badge" :class="sessionTypeClass">
              {{ selectedSession.__typename || "Session" }}
            </span>
          </h4>
          <div class="session-meta">
            <div class="meta-item">
              <i class="icon-calendar"></i>
              <span>{{ formatDate(selectedSession.startTime) }}</span>
            </div>
            <div class="meta-item">
              <i class="icon-clock"></i>
              <span>{{ formatTime(selectedSession.startTime) }} -
                {{ formatTime(selectedSession.endTime) }}</span>
            </div>
          </div>
          <!-- Match-specific info -->
          <div v-if="isMatchSession" class="match-info">
            <div class="match-score">
              <span class="team">{{ selectedSession.ourTeam || "Us" }}</span>
              <span class="score">{{ selectedSession.score || 0 }} -
                {{ selectedSession.opponentScore || 0 }}</span>
              <span class="team">{{
                selectedSession.opponent || "Opposition"
                }}</span>
            </div>
            <span class="result-badge" :class="matchResultClass">
              {{ selectedSession.result || "UNKNOWN" }}
            </span>
          </div>
        </div>
      </div>

      <!-- Overview Summary -->
      <div class="overview-summary">
        <h5>Session Summary</h5>
        <div class="summary-cards">
          <div class="summary-card">
            <div class="card-value">
              {{ formatDistance(sessionAverages.avgDistance) }}
            </div>
            <div class="card-label">Avg Distance</div>
          </div>
          <div class="summary-card">
            <div class="card-value">
              {{ formatDistance(sessionAverages.avgSprintDistance) }}
            </div>
            <div class="card-label">Avg Sprint Distance</div>
          </div>
          <div class="summary-card">
            <div class="card-value">
              {{ formatDistance(sessionAverages.avgHighIntensity) }}
            </div>
            <div class="card-label">Avg High Intensity</div>
          </div>
          <div class="summary-card">
            <div class="card-value">{{ athleteData.length }}</div>
            <div class="card-label">Athletes</div>
          </div>
        </div>
      </div>

      <!-- Charts Section -->
      <div class="charts-section">
        <!-- Chart 1: Total Match Distance -->
        <div class="chart-container">
          <h5>Total Distance</h5>
          <div class="chart-wrapper">
            <div v-for="athlete in sortedAthletesByDistance" :key="athlete.id" class="bar-row">
              <div class="athlete-name clickable" @click="openPlayerDetail(athlete)"
                :title="'Click to view ' + athlete.name + ' details'">
                {{ athlete.name }}
              </div>
              <div class="bar-container">
                <div class="bar total-distance" :style="{
                  width: getBarWidth(athlete.totalDistance, maxTotalDistance),
                }" :title="`${formatDistance(athlete.totalDistance)}`">
                  <span class="bar-label">{{
                    formatDistance(athlete.totalDistance)
                    }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Chart 2: First Half Distance (Match Only) -->
        <div v-if="isMatchSession && hasHalfData" class="chart-container">
          <h5>First Half Distance</h5>
          <div class="chart-wrapper">
            <div v-for="athlete in sortedAthletesByFirstHalf" :key="athlete.id" class="bar-row">
              <div class="athlete-name clickable" @click="openPlayerDetail(athlete)"
                :title="'Click to view ' + athlete.name + ' details'">
                {{ athlete.name }}
              </div>
              <div class="bar-container">
                <div class="bar first-half" :style="{
                  width: getBarWidth(
                    athlete.firstHalfDistance,
                    maxFirstHalfDistance,
                  ),
                }" :title="`${formatDistance(athlete.firstHalfDistance)}`">
                  <span class="bar-label">{{
                    formatDistance(athlete.firstHalfDistance)
                    }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Chart 3: Second Half Distance (Match Only) -->
        <div v-if="isMatchSession && hasHalfData" class="chart-container">
          <h5>Second Half Distance</h5>
          <div class="chart-wrapper">
            <div v-for="athlete in sortedAthletesBySecondHalf" :key="athlete.id" class="bar-row">
              <div class="athlete-name clickable" @click="openPlayerDetail(athlete)"
                :title="'Click to view ' + athlete.name + ' details'">
                {{ athlete.name }}
              </div>
              <div class="bar-container">
                <div class="bar second-half" :style="{
                  width: getBarWidth(
                    athlete.secondHalfDistance,
                    maxSecondHalfDistance,
                  ),
                }" :title="`${formatDistance(athlete.secondHalfDistance)}`">
                  <span class="bar-label">{{
                    formatDistance(athlete.secondHalfDistance)
                    }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Chart 4: Speed Zones (Stacked Horizontal Bars) -->
        <div class="chart-container speed-zones">
          <h5>Speed Zones Distribution</h5>
          <div class="zone-legend">
            <span class="legend-item" v-for="zone in speedZoneConfig" :key="zone.key">
              <span class="legend-color" :style="{ backgroundColor: zone.color }"></span>
              {{ zone.label }}
            </span>
          </div>
          <div class="chart-wrapper">
            <div v-for="athlete in sortedAthletesBySpeedZones" :key="athlete.id" class="bar-row">
              <div class="athlete-name clickable" @click="openPlayerDetail(athlete)"
                :title="'Click to view ' + athlete.name + ' details'">
                {{ athlete.name }}
              </div>
              <div class="bar-container">
                <div class="stacked-bar">
                  <div v-for="zone in speedZoneConfig" :key="zone.key" class="zone-segment" :style="{
                    width:
                      getZonePercentage(
                        athlete.speedZones[zone.key],
                        athlete.totalZoneTime,
                      ) + '%',
                    backgroundColor: zone.color,
                  }" :title="`${zone.label}: ${formatTime(athlete.speedZones[zone.key])}`">
                    <span v-if="
                      getZonePercentage(
                        athlete.speedZones[zone.key],
                        athlete.totalZoneTime,
                      ) > 10
                    " class="zone-label">
                      {{ formatTime(athlete.speedZones[zone.key]) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Chart 5: Acceleration & Deceleration Events -->
        <div class="chart-container acceleration-chart">
          <h5>Acceleration & Deceleration Events</h5>
          <div class="event-legend">
            <span class="legend-item">
              <span class="legend-color" style="background-color: #4caf50"></span>
              Acceleration Events
            </span>
            <span class="legend-item">
              <span class="legend-color" style="background-color: #f44336"></span>
              Deceleration Events
            </span>
          </div>
          <div class="chart-wrapper">
            <div v-for="athlete in sortedAthletesByAcceleration" :key="athlete.id" class="bar-row">
              <div class="athlete-name clickable" @click="openPlayerDetail(athlete)"
                :title="'Click to view ' + athlete.name + ' details'">
                {{ athlete.name }}
              </div>
              <div class="bar-container dual-bars">
                <!-- Acceleration Bar -->
                <div class="bar-wrapper">
                  <div class="bar acceleration" :style="{
                    width: getBarWidth(
                      athlete.accelerationEvents,
                      maxAccelerationEvents,
                    ),
                  }" :title="`Acceleration: ${athlete.accelerationEvents} events`">
                    <span class="bar-label">{{
                      athlete.accelerationEvents
                      }}</span>
                  </div>
                </div>
                <!-- Deceleration Bar -->
                <div class="bar-wrapper">
                  <div class="bar deceleration" :style="{
                    width: getBarWidth(
                      athlete.decelerationEvents,
                      maxDecelerationEvents,
                    ),
                  }" :title="`Deceleration: ${athlete.decelerationEvents} events`">
                    <span class="bar-label">{{
                      athlete.decelerationEvents
                      }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No Data State -->
    <div v-else-if="selectedSessionId && !loadingStuff" class="no-data">
      <p>No athlete data available for this session.</p>
    </div>

    <!-- Player Detail Modal -->
    <player-gps-detail-modal :show="showPlayerModal" :athlete="selectedAthlete" :all-athletes="athleteData"
      :session-id="selectedSessionId" :session-data="selectedSession || {}" :session-averages="sessionAverages"
      @close="closePlayerModal" />
  </div>
</template>

<script>
import SpinnerLoader from "../LoadingSpinning";
import PlayerGpsDetailModal from "./PlayerGpsDetailModal.vue";
import { playerDataApi } from "../../utils/playerDataBackend";
import { PlayerData } from "@/services/playerData-service.js";

export default {
  name: "GpsReports",
  components: {
    SpinnerLoader,
    PlayerGpsDetailModal,
  },
  props: {
    userData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loadingStuff: false,
      teams: [],
      selectedTeamId: null,
      sessions: [],
      historicalDataSynced: false,
      selectedSessionId: null,
      selectedSession: null,
      athleteData: [],
      error: null,
      showPlayerModal: false,
      selectedAthlete: null,
      speedZoneConfig: [
        {
          key: "stationary",
          label: "Stationary/Walk",
          color: "#e0e0e0",
          max: 8.47,
        },
        { key: "jogging", label: "Jogging", color: "#90caf9", max: 12.7 },
        {
          key: "lowIntensity",
          label: "Low Int. Run",
          color: "#66bb6a",
          max: 16.94,
        },
        {
          key: "mediumIntensity",
          label: "Med Int. Run",
          color: "#ffa726",
          max: 23.29,
        },
        {
          key: "highIntensity",
          label: "High Int. Run",
          color: "#ef5350",
          max: 30.49,
        },
        { key: "sprint", label: "Sprint", color: "#ab47bc", max: Infinity },
      ],
    };
  },
  computed: {
    isMatchSession() {
      return this.selectedSession?.__typename === "MatchSession";
    },
    sessionTypeClass() {
      return this.isMatchSession ? "match-session" : "training-session";
    },
    matchResultClass() {
      const result = this.selectedSession?.result;
      return {
        "result-win": result === "WIN",
        "result-draw": result === "DRAW",
        "result-loss": result === "LOSS",
      };
    },
    hasHalfData() {
      return this.athleteData.some(
        (a) => a.firstHalfDistance > 0 || a.secondHalfDistance > 0,
      );
    },
    sessionAverages() {
      if (this.athleteData.length === 0) {
        return { avgDistance: 0, avgSprintDistance: 0, avgHighIntensity: 0 };
      }

      const sum = this.athleteData.reduce(
        (acc, athlete) => ({
          distance: acc.distance + athlete.totalDistance,
          sprint: acc.sprint + athlete.sprintDistance,
          highIntensity: acc.highIntensity + athlete.highIntensityDistance,
        }),
        { distance: 0, sprint: 0, highIntensity: 0 },
      );

      const count = this.athleteData.length;
      return {
        avgDistance: sum.distance / count,
        avgSprintDistance: sum.sprint / count,
        avgHighIntensity: sum.highIntensity / count,
      };
    },
    sortedAthletesByDistance() {
      return [...this.athleteData].sort(
        (a, b) => b.totalDistance - a.totalDistance,
      );
    },
    sortedAthletesByFirstHalf() {
      return [...this.athleteData].sort(
        (a, b) => b.firstHalfDistance - a.firstHalfDistance,
      );
    },
    sortedAthletesBySecondHalf() {
      return [...this.athleteData].sort(
        (a, b) => b.secondHalfDistance - a.secondHalfDistance,
      );
    },
    sortedAthletesBySpeedZones() {
      return [...this.athleteData].sort(
        (a, b) => b.totalZoneTime - a.totalZoneTime,
      );
    },
    maxTotalDistance() {
      return Math.max(...this.athleteData.map((a) => a.totalDistance), 1);
    },
    maxFirstHalfDistance() {
      return Math.max(...this.athleteData.map((a) => a.firstHalfDistance), 1);
    },
    maxSecondHalfDistance() {
      return Math.max(...this.athleteData.map((a) => a.secondHalfDistance), 1);
    },
    sortedAthletesByAcceleration() {
      // Sort by total acceleration + deceleration events (descending)
      return [...this.athleteData].sort((a, b) => {
        const totalA = a.accelerationEvents + a.decelerationEvents;
        const totalB = b.accelerationEvents + b.decelerationEvents;
        return totalB - totalA;
      });
    },
    maxAccelerationEvents() {
      return Math.max(...this.athleteData.map((a) => a.accelerationEvents), 1);
    },
    maxDecelerationEvents() {
      return Math.max(...this.athleteData.map((a) => a.decelerationEvents), 1);
    },
  },
  async mounted() {
    await this.loadTeams();
  },
  methods: {
    async loadTeams() {
      this.loadingStuff = true;
      this.error = null;

      try {
        const response = await PlayerData.find({
          playerId: this.userData.id,
          action: "getAllClubTeamsFromPlayerData",
        });

        // Handle both nested and flat response formats
        let teams = response.data?.data;
        if (teams?.data && Array.isArray(teams.data)) {
          // Nested format: { success, data: { success, data: [...] } }
          teams = teams.data;
        }

        if (Array.isArray(teams) && teams.length > 0) {
          this.teams = teams;
        } else {
          this.error = "No teams found.";
        }
      } catch (error) {
        console.error("Failed to load teams:", error);
        this.error = `Failed to load teams: ${error.message}`;
      }

      this.loadingStuff = false;
    },

    async onTeamChange() {
      this.sessions = [];
      this.selectedSessionId = null;
      this.selectedSession = null;
      this.athleteData = [];
      this.historicalDataSynced = false;

      if (!this.selectedTeamId) return;

      await this.fetchSessions();
    },

    async fetchSessions() {
      this.loadingStuff = true;
      this.error = null;

      try {
        const response = await playerDataApi.getTeamSessions(
          this.selectedTeamId,
        );

        if (response.success && response.data?.team?.sessions) {
          this.sessions = response.data.team.sessions;

          if (this.sessions.length === 0) {
            this.error = "No sessions found for this team.";
          }
        } else {
          this.error = "Failed to load sessions.";
        }
      } catch (error) {
        console.error("Error fetching sessions:", error);
        this.error = `Failed to load sessions: ${error.message}`;
      }

      this.loadingStuff = false;
    },

    async onSessionChange() {
      this.athleteData = [];

      if (!this.selectedSessionId) {
        this.selectedSession = null;
        return;
      }

      // Find the selected session object
      this.selectedSession = this.sessions.find(
        (s) => s.id === this.selectedSessionId,
      );

      await this.fetchAthleteMetrics();
    },

    async fetchAthleteMetrics() {
      this.loadingStuff = true;
      this.error = null;

      try {
        const response = await playerDataApi.getAthleteMetrics(
          this.selectedSessionId,
        );

        console.log("Full response:", response); // Enhanced debug log

        // Check the correct path - participations is at response.data.participations
        if (response.success && response.data?.participations) {
          if (response.data.participations.length === 0) {
            this.error = "No participations found for this session.";
          } else {
            this.processAthleteData(response.data.participations);
          }
        } else {
          this.error = "No athlete metrics found for this session.";
          console.log("Response structure:", response);
        }
      } catch (error) {
        console.error("Error fetching athlete metrics:", error);
        this.error = `Failed to load athlete metrics: ${error.message}`;
      }

      this.loadingStuff = false;
    },

    processAthleteData(participations) {
      console.log("Raw participations data:", participations); // Debug log

      this.athleteData = participations.map((participation) => {
        // Handle different metric structures for Match vs Training sessions
        let metricSet = {};

        if (participation.__typename === "MatchSessionParticipation") {
          metricSet = participation.metricSet || {};
        } else if (
          participation.__typename === "TrainingSessionParticipation"
        ) {
          // Training sessions use athleteMetricSet instead of metricSet
          metricSet = participation.athleteMetricSet || {};
        }

        const periodMetrics = participation.periodMetricSets || [];

        // Add validation for missing data
        if (!metricSet || Object.keys(metricSet).length === 0) {
          console.warn(
            `Missing metric data for athlete ${participation.athlete?.name}`,
            participation,
          );
        }

        // Extract period data (assuming first period = first half, second = second half)
        const firstHalf = periodMetrics[0] || {};
        const secondHalf = periodMetrics[1] || {};

        // Process speed zones from timeAtKph array
        const speedZones = this.processSpeedZones(metricSet.timeAtKph || []);

        return {
          id: participation.id,
          athleteId: participation.athlete?.id || null, // Store actual athlete ID
          name: participation.athlete?.name || "Unknown",
          initials: participation.athlete?.initials || "??",
          totalDistance: metricSet.totalDistanceM || 0,
          sprintDistance: metricSet.totalSprintDistanceM || 0,
          highIntensityDistance: metricSet.totalHighIntensityDistanceM || 0,
          maxSpeed: metricSet.maxSpeedKph || 0,
          avgSpeed: metricSet.avgSpeedKph || 0,
          sprintEvents: metricSet.sprintEvents || 0,
          metresPerMinute: metricSet.metresPerMinute || 0,
          accelerationEvents: metricSet.accelerationEvents || 0,
          decelerationEvents: metricSet.decelerationEvents || 0,
          firstHalfDistance: firstHalf.totalDistanceM || 0,
          secondHalfDistance: secondHalf.totalDistanceM || 0,
          speedZones,
          totalZoneTime: Object.values(speedZones).reduce(
            (sum, val) => sum + val,
            0,
          ),
        };
      });
    },

    processSpeedZones(timeAtKphArray) {
      // timeAtKph is an array of objects like:
      // [{ endExclusive: 8.47, value: 75.37 }, { endExclusive: 12.7, value: 13.07 }, ...]
      // where value is percentage of time spent in that zone

      const zones = {
        stationary: 0, // 0 - 8.47 km/h
        jogging: 0, // 8.47 - 12.7 km/h
        lowIntensity: 0, // 12.7 - 16.94 km/h
        mediumIntensity: 0, // 16.94 - 23.29 km/h
        highIntensity: 0, // 23.29 - 30.49 km/h
        sprint: 0, // 30.49+ km/h
      };

      if (!timeAtKphArray || timeAtKphArray.length === 0) return zones;

      // Map the speed buckets to our zone keys
      timeAtKphArray.forEach((bucket, index) => {
        const zoneKey = this.speedZoneConfig[index]?.key;
        if (zoneKey && Object.prototype.hasOwnProperty.call(zones, zoneKey)) {
          zones[zoneKey] = bucket.value || 0; // value is percentage
        }
      });

      return zones;
    },

    getBarWidth(value, maxValue) {
      if (maxValue === 0) return "0%";
      return `${(value / maxValue) * 100}%`;
    },

    getZonePercentage(zoneValue, totalTime) {
      if (totalTime === 0) return 0;
      return zoneValue; // Already a percentage from API
    },

    formatDistance(meters) {
      if (!meters) return "0m";
      if (meters >= 1000) {
        return `${(meters / 1000).toFixed(2)}km`;
      }
      return `${Math.round(meters)}m`;
    },

    formatTime(value) {
      if (!value) return "0:00";

      // If value is a time string, parse it
      if (typeof value === "string") {
        const date = new Date(value);
        return date.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
        });
      }

      // If value is minutes (for zone times), format as MM:SS
      const minutes = Math.floor(value);
      const seconds = Math.round((value - minutes) * 60);
      return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    },

    formatDate(dateStr) {
      if (!dateStr) return "Unknown";
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    formatSessionOption(session) {
      const date = this.formatDate(session.startTime);
      const type =
        session.__typename === "MatchSession" ? "⚽ Match" : "🏃 Training";

      if (session.__typename === "MatchSession" && session.opponent) {
        return `${type} vs ${session.opponent} - ${date}`;
      }

      return `${type} - ${date}`;
    },

    async openPlayerDetail(athlete) {
      console.log(
        "Opening player detail, historicalDataSynced:",
        this.historicalDataSynced,
        "selectedTeamId:",
        this.selectedTeamId,
      );
      this.selectedAthlete = athlete;
      this.showPlayerModal = true;

      // Auto-sync historical data on first player click for this team (fire and forget)
      if (!this.historicalDataSynced && this.selectedTeamId) {
        console.log("Triggering auto-sync...");
        this.historicalDataSynced = true; // Set immediately to prevent duplicate syncs
        this.syncHistoricalSessions(); // Don't await - let it run in background
      }
    },

    closePlayerModal() {
      this.showPlayerModal = false;
      this.selectedAthlete = null;
    },

    async syncHistoricalSessions() {
      if (!this.selectedTeamId) {
        console.log("No selectedTeamId, skipping sync");
        return;
      }

      console.log("Starting sync for team:", this.selectedTeamId);
      this.syncingHistory = true;

      try {
        const response = await playerDataApi.syncHistoricalSessions(
          this.selectedTeamId,
        );
        console.log("Sync response:", response);

        if (response.success || response.data?.success) {
          const result = response.data || response;
          console.log("Sync result:", result);
          this.$toast?.success(
            `✅ Synced ${result.synced} of ${result.total} sessions!`,
          );

          // Refresh sessions list
          await this.fetchSessions();
        } else {
          this.$toast?.error("Failed to sync historical sessions");
        }
      } catch (error) {
        console.error("Error syncing historical sessions:", error);
        this.$toast?.error(`Sync failed: ${error.message}`);
      } finally {
        this.syncingHistory = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.gps-reports-container {
  background: #282b33;
  padding: 2rem;
  border-radius: 8px;
}

.header {
  text-align: center;
  margin-bottom: 30px;

  h3 {
    color: #ffffff;
    margin-bottom: 5px;
    font-size: 2rem;
    font-weight: 700;
  }

  .subtitle {
    color: #bbbbbb;
    font-size: 1.1rem;
  }
}

.alert {
  padding: 15px 20px;
  margin-bottom: 20px;
  border-radius: 8px;
  position: relative;

  &.alert-danger {
    color: #721c24;
    background-color: #f8d7da;
    border: 1px solid #f5c6cb;
  }

  .close-btn {
    position: absolute;
    right: 10px;
    top: 10px;
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #721c24;
    opacity: 0.7;

    &:hover {
      opacity: 1;
    }
  }
}

.selection-group {
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  flex-wrap: wrap;

  .select-wrapper {
    flex: 1;
    min-width: 250px;

    label {
      display: block;
      font-weight: 600;
      color: #bcbabc;
      margin-bottom: 8px;
      font-size: 0.95rem;
    }

    select {
      width: 100%;
      padding: 12px 15px;
      border: 2px solid #464a5a;
      border-radius: 8px;
      background-color: #1f2128;
      font-size: 1rem;
      cursor: pointer;
      color: white;
      transition: all 0.3s ease;

      &:hover:not(:disabled) {
        border-color: #667eea;
      }

      &:focus {
        outline: none;
        border-color: #464a5a;
        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
      }

      &:disabled {
        background-color: #f5f5f5;
        cursor: not-allowed;
        opacity: 0.6;
      }
    }
  }
}

.session-details {
  margin-top: 30px;
}

.session-header {
  background: #3a3c43;
  color: white;
  padding: 2rem;
  border-radius: 12px;
  margin-bottom: 2rem;

  .session-type-badge {
    display: inline-block;
    padding: 6px 16px;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 600;

    &.match-session {
      background-color: rgba(255, 255, 255, 0.25);
    }

    &.training-session {
      background-color: rgba(255, 255, 255, 0.25);
    }
  }

  .session-meta {
    display: flex;
    gap: 20px;
    margin-top: 10px;
    flex-wrap: wrap;

    .meta-item {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 0.95rem;
    }
  }

  .match-info {
    margin-top: 15px;
    padding-top: 15px;
    border-top: 1px solid rgba(255, 255, 255, 0.3);

    .match-score {
      display: flex;
      align-items: center;
      gap: 15px;
      font-size: 1.3rem;
      font-weight: 700;
      margin-bottom: 10px;

      .score {
        font-size: 1.5rem;
        letter-spacing: 2px;
      }
    }

    .result-badge {
      display: inline-block;
      padding: 4px 12px;
      border-radius: 12px;
      font-size: 0.85rem;
      font-weight: 700;

      &.result-win {
        background-color: #4caf50;
      }

      &.result-draw {
        background-color: #ff9800;
      }

      &.result-loss {
        background-color: #f44336;
      }
    }
  }
}

.overview-summary {
  margin-bottom: 30px;

  h5 {
    font-size: 1.3rem;
    color: #2c3e50;
    margin-bottom: 15px;
    font-weight: 700;
  }

  .summary-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 15px;

    .summary-card {
      background: #3a3b43;
      border: 2px solid #65666f;
      border-radius: 10px;
      padding: 20px;
      text-align: center;
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        border-color: #37cbf5;
      }

      .card-value {
        font-size: 1.8rem;
        font-weight: 700;
        color: #37cbf5;
        margin-bottom: 5px;
      }

      .card-label {
        font-size: 0.9rem;
        color: #7f8c8d;
        font-weight: 500;
      }
    }
  }
}

.charts-section {
  display: flex;
  flex-direction: column;
  gap: 30px;

  .chart-container {
    background: #3a3b43;
    border-radius: 12px;
    padding: 25px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    h5 {
      font-size: 1.2rem;
      color: #ffffff;
      margin-bottom: 20px;
      font-weight: 700;
    }

    .chart-wrapper {
      display: flex;
      flex-direction: column;
      gap: 12px;

      .bar-row {
        display: flex;
        align-items: center;
        gap: 15px;

        .athlete-name {
          min-width: 150px;
          font-weight: 600;
          color: #ffffff;
          font-size: 0.95rem;

          &.clickable {
            cursor: pointer;
            transition: all 0.2s ease;
            padding: 4px 8px;
            border-radius: 4px;

            &:hover {
              color: #39caf5;
              background: rgba(57, 202, 245, 0.1);
              transform: translateX(3px);
            }
          }
        }

        .bar-container {
          flex: 1;
          position: relative;

          .bar {
            height: 36px;
            border-radius: 6px;
            display: flex;
            align-items: center;
            padding: 0 12px;
            transition: all 0.3s ease;
            min-width: 50px;

            &.total-distance {
              background: linear-gradient(90deg, #39caf5, #0497c2);
            }

            &.first-half {
              background: linear-gradient(90deg, #4caf50, #45a049);
            }

            &.second-half {
              background: linear-gradient(90deg, #2196f3, #1976d2);
            }

            .bar-label {
              color: white;
              font-weight: 600;
              font-size: 0.9rem;
              white-space: nowrap;
            }

            &:hover {
              opacity: 0.9;
              transform: scaleY(1.05);
            }
          }

          .stacked-bar {
            display: flex;
            width: 100%;
            height: 36px;
            border-radius: 6px;
            overflow: hidden;

            .zone-segment {
              display: flex;
              align-items: center;
              justify-content: center;
              transition: all 0.2s ease;

              .zone-label {
                color: white;
                font-size: 0.75rem;
                font-weight: 600;
                text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
              }

              &:hover {
                opacity: 0.85;
              }
            }
          }
        }
      }
    }

    &.speed-zones,
    &.acceleration-chart {

      .zone-legend,
      .event-legend {
        display: flex;
        flex-wrap: wrap;
        gap: 15px;
        margin-bottom: 20px;
        padding: 15px;
        background: #32333d;
        border-radius: 8px;
        color: #e1e0e0;

        .legend-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.85rem;
          font-weight: 500;

          .legend-color {
            width: 20px;
            height: 20px;
            border-radius: 4px;
            border: 1px solid rgba(0, 0, 0, 0.1);
          }
        }
      }
    }

    &.acceleration-chart {
      .bar-container.dual-bars {
        display: flex;
        flex-direction: column;
        gap: 8px;

        .bar-wrapper {
          flex: 1;

          .bar {
            &.acceleration {
              background: linear-gradient(90deg, #4caf50, #45a049);
            }

            &.deceleration {
              background: linear-gradient(90deg, #f44336, #d32f2f);
            }
          }
        }
      }
    }
  }
}

.no-data {
  text-align: center;
  padding: 60px 20px;
  color: #7f8c8d;
  font-size: 1.1rem;
}

@media (max-width: 768px) {
  .gps-reports-container {
    padding: 15px;
  }

  .selection-group {
    flex-direction: column;

    .select-wrapper {
      min-width: 100%;
    }
  }

  .session-header {
    padding: 20px;

    .match-info .match-score {
      flex-direction: column;
      gap: 10px;
      font-size: 1.1rem;
    }
  }

  .overview-summary .summary-cards {
    grid-template-columns: 1fr 1fr;
  }

  .charts-section .chart-container {
    padding: 15px;

    .chart-wrapper .bar-row {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;

      .athlete-name {
        min-width: 100%;
      }

      .bar-container {
        width: 100%;
      }
    }
  }
}
</style>
